<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('attendance.attendance_adjustment')">
      <oxd-form @submit-valid="filterItems" @reset="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <employee-autocomplete
                v-model="filters.employee"
                :rules="rules.employee"
                :params="{
                  includeEmployees: filters.includeEmployees?.param,
                }"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="filters.status"
                type="select"
                :label="$t('attendance.attendance_my_adjustment_status')"
                :options="includeOpts"
                :show-empty-selector="false"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            type="reset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <br />
    <div class="orangehrm-paper-container">
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          ref="cardTable"
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="items?.data"
          :loading="isLoading"
          class="orangehrm-employee-list"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
    <confirmation-dialog
      ref="overlapReplaceDialog"
      :title="overlapReplaceDialogTitle"
      :subtitle="overlapReplaceDialogSubtitle"
      :cancel-label="$t('general.no_cancel')"
      :confirm-label="$t('general.confirm')"
      icon="info-circle"
      confirm-button-type="secondary"
    />
  </div>
  <attendance-adjustment-comments-modal
    v-if="showCommentModal"
    :id="commentModalState"
    @close="onCommentModalClose"
  />
</template>

<script>
// import usei18n from '@/core/util/composable/usei18n';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {APIService} from '@/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';
import {computed, ref} from 'vue';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import ConfirmationDialog from '@ohrm/components/dialogs/ConfirmationDialog';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';
import useAdjustmentActions, {
  getAdjustmentActionErrorMessage,
  parseAdjustmentApprovalOverlapRecordId,
} from '@/orangehrmAttendancePlugin/util/useAdjustmentActions';
import {formatEmployeePunchTime} from '@/orangehrmAttendancePlugin/util/formatEmployeePunchTime';
import useDateFormat from '@/core/util/composable/useDateFormat';
import useLocale from '@/core/util/composable/useLocale';
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import AttendanceAdjustmentCommentsModal from '@/orangehrmAttendancePlugin/components/AttendanceAdjustmentCommentsModal';
import ShiftPermissionCommentCountCell from '@/orangehrmLeavePlugin/components/ShiftPermissionCommentCountCell';

const defaultSortOrder = {
  'employee.employeeId': 'DEFAULT',
};

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'confirmation-dialog': ConfirmationDialog,
    'employee-autocomplete': EmployeeAutocomplete,
    'attendance-adjustment-comments-modal': AttendanceAdjustmentCommentsModal,
  },
  props: {
    employee: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const {locale} = useLocale();
    const {jsDateFormat, jsTimeFormat} = useDateFormat();

    const dataNormalizer = (data) => {
      return data.map((item) => {
        const punchInTime = formatEmployeePunchTime({
          utcTime: item.punchInUtcTime,
          timezoneName: item.punchInTimezoneName,
          userTime: item.punchInUserTime,
          jsDateFormat,
          jsTimeFormat,
          locale,
        });
        const punchOutTime = formatEmployeePunchTime({
          utcTime: item.punchOutUtcTime,
          timezoneName: item.punchOutTimezoneName,
          fallbackTimezoneName: item.punchInTimezoneName,
          userTime: item.punchOutUserTime,
          jsDateFormat,
          jsTimeFormat,
          locale,
        });

        return {
          fullName: formatEmployeeFullName(item),
          id: item.id,
          employeeId: item.employeeId,
          punchInTimezoneName: item.punchInTimezoneName,
          punchInUserTime: item.punchInUserTime,
          punchOutTimezoneName: item.punchOutTimezoneName,
          punchOutUserTime: item.punchOutUserTime,
          punchInTime: punchInTime,
          punchOutTime: punchOutTime,
          note: item.note,
          status: item.status,
          statusName: item.statusName,
          commentCount: item.commentCount ?? 0,
          actions: [
            {
              action: 'APPROVE',
              name: 'Approve',
            },
            {
              action: 'REJECT',
              name: 'Reject',
            },
          ],
        };
      });
    };
    const includeOpts = [
      {
        id: 1,
        param: '',
        label: 'All',
      },
      {
        id: 2,
        param: '1',
        label: 'Pending',
      },
      {
        id: 3,
        param: '2',
        label: 'Sent',
      },
      {
        id: 4,
        param: '3',
        label: 'Revision',
      },
      {
        id: 5,
        param: '4',
        label: 'Rejected',
      },
      {
        id: 6,
        param: '5',
        label: 'Approved',
      },
      {
        id: 7,
        param: '6',
        label: 'Cancelled',
      },
    ];

    const statusMap = includeOpts.reduce((acc, cur) => {
      acc[cur.label] = Number(cur.param || 0);
      return acc;
    }, {});
    const filters = ref({
      status: includeOpts.find((opt) => opt.param === '1'),
    });
    const {sortDefinition, sortField, sortOrder, onSort} = useSort({
      sortDefinition: defaultSortOrder,
    });
    const serializedFilters = computed(() => {
      return {
        status: filters.value.status?.param,
        sortField: sortField.value,
        sortOrder: sortOrder.value,
        employeeId: filters.value.employee?.id,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/adjust-approval',
    );
    http.setIgnorePath('adjust-approval');
    const attendanceRecordHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/records',
    );

    const {adjustmentActions, processAdjustmentAction} =
      useAdjustmentActions(http);

    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: dataNormalizer,
    });

    onSort(execQuery);

    return {
      http,
      attendanceRecordHttp,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: response,
      filters,
      sortDefinition,
      includeOpts,
      statusMap,
      adjustmentActions,
      processAdjustmentAction,
    };
  },
  data() {
    return {
      checkedItems: [],
      showCommentModal: false,
      commentModalState: null,
      rules: {
        employee: [],
      },
      overlapReplaceDialogTitle: 'Replace existing attendance?',
      overlapReplaceDialogSubtitle: '',
    };
  },
  computed: {
    headers() {
      return [
        {
          name: 'fullName',
          title: this.$t('general.name'),
          sortField: 'fullName',
          style: {flex: 1},
        },
        {
          name: 'punchInTime',
          title: this.$t('attendance.punch_in'),
          sortField: 'punchInTime',
          style: {flex: 1},
        },
        {
          name: 'punchOutTime',
          title: this.$t('attendance.punch_out'),
          sortField: 'punchOutTime',
          style: {flex: 1},
        },
        {
          name: 'punchInTimezoneName',
          title: this.$t('attendance.timezone_in'),
          sortField: 'punchInTimezoneName',
          style: {flex: 1},
        },
        {
          name: 'punchOutTimezoneName',
          title: this.$t('attendance.timezone_out'),
          sortField: 'punchOutTimezoneName',
          style: {flex: 1},
        },
        {
          name: 'statusName',
          title: this.$t('general.status'),
          sortField: 'statusName',
          style: {flex: 1},
        },
        {
          name: 'commentCount',
          title: this.$t('general.comments'),
          style: {flex: 1},
          cellRenderer: this.commentCountCellRenderer,
        },
        {
          name: 'action',
          slot: 'footer',
          title: this.$t('general.actions'),
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.cellRenderer,
          style: {
            flex: '10%',
          },
        },
      ];
    },
  },

  methods: {
    commentCountCellRenderer(index, count, header, row) {
      return {
        component: ShiftPermissionCommentCountCell,
        props: {
          count: count ?? 0,
          onClick: () => this.openCommentsModal(row),
        },
      };
    },
    openCommentsModal(row) {
      this.commentModalState = row.id;
      this.showCommentModal = true;
    },
    onCommentModalClose() {
      this.commentModalState = null;
      this.showCommentModal = false;
      this.execQuery();
    },
    cellRenderer(...[, , , row]) {
      const cellConfig = {};
      const {approve, reject} = this.adjustmentActions;

      const isNotOwnRequest =
        Number(row.employeeId) !== Number(this.employee.empNumber);

      if (row.statusName.toLowerCase() === 'pending' && isNotOwnRequest) {
        row.actions.forEach((item) => {
          if (item.action === 'APPROVE') {
            approve.props.label = this.$t('general.approve');
            approve.props.onClick = () =>
              this.onAdjustmentAction(row.id, this.statusMap['Approved'], row);
            cellConfig.approve = approve;
          }

          if (item.action === 'REJECT') {
            reject.props.label = this.$t('general.reject');
            reject.props.onClick = () =>
              this.onAdjustmentAction(row.id, this.statusMap['Rejected'], row);
            cellConfig.reject = reject;
          }
        });
      }

      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },

    onAdjustmentDropdownAction(event, item) {
      switch (event.context) {
        case 'cancel_adjustment':
          this.onAdjustmentAction(item.id, this.statusMap['Rejected']);
      }
    },
    formatAttendanceRecordSummary(record) {
      const punchIn = record?.punchIn;
      const punchOut = record?.punchOut;
      const inText =
        punchIn?.userDate && punchIn?.userTime
          ? `${punchIn.userDate} ${punchIn.userTime}`
          : '-';
      const outText =
        punchOut?.userDate && punchOut?.userTime
          ? `${punchOut.userDate} ${punchOut.userTime}`
          : '-';
      return `${inText} – ${outText}`;
    },
    async fetchAttendanceRecordSummary(attendanceRecordId) {
      try {
        const response = await this.attendanceRecordHttp.get(
          attendanceRecordId,
        );
        return this.formatAttendanceRecordSummary(response?.data?.data);
      } catch {
        return null;
      }
    },
    async confirmReplaceOverlappingRecord(attendanceRecordId) {
      const summary = await this.fetchAttendanceRecordSummary(
        attendanceRecordId,
      );
      this.overlapReplaceDialogSubtitle = summary
        ? `This adjustment overlaps existing attendance (${summary}). Approve and replace that record with the requested times?`
        : 'This adjustment overlaps existing attendance on this date. Approve and replace that record with the requested times?';
      await this.$nextTick();
      const confirmation = await this.$refs.overlapReplaceDialog?.showDialog();
      return confirmation === 'ok';
    },
    async onAdjustmentAction(
      id,
      statusId,
      row = null,
      replaceAttendanceId = null,
    ) {
      this.isLoading = true;
      try {
        await this.processAdjustmentAction(id, statusId, replaceAttendanceId);
        await this.$toast.updateSuccess();
        await this.resetDataTable();
      } catch (error) {
        this.isLoading = false;
        const message = getAdjustmentActionErrorMessage(error);
        const overlapRecordId = parseAdjustmentApprovalOverlapRecordId(message);
        if (
          overlapRecordId &&
          statusId === this.statusMap['Approved'] &&
          !replaceAttendanceId
        ) {
          const confirmed = await this.confirmReplaceOverlappingRecord(
            overlapRecordId,
          );
          if (confirmed) {
            return this.onAdjustmentAction(id, statusId, row, overlapRecordId);
          }
          return;
        }

        if (!parseAdjustmentApprovalOverlapRecordId(message)) {
          this.$toast.unexpectedError(message);
        }
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.card-footer-slot) {
  padding-top: 0;

  .oxd-table-cell {
    flex: 0 0 auto !important;
  }

  .oxd-table-cell-actions {
    justify-content: flex-start;
  }

  .oxd-table-cell-actions > * {
    margin: 0 !important;
  }
}

.oxd-export-btn {
  margin-right: 4px;
}
</style>
